<script setup lang="ts">
// 通用错误页（403 / 404 共用）。
//
// 为什么抽成一个组件：View403 与 View404 原本是两份逐字重复的极简模板
// （一段路径 + 一句话 + 一个按钮），只美化其中一份会让两个错误页明显不一致。
//
// 关于 HTTP 状态码：本站是纯 SPA，状态码由托管层决定，客户端路由渲染出的页面
// 一律是 200（详见 docs 与托管平台说明）。因此「这是一个错误页」只能靠三处表达：
//   1. <title>：由路由 meta.title 驱动（router/index.ts），App.vue 负责拼站点名；
//   2. <h1> 与正文文案：本组件渲染；
//   3. meta robots=noindex：util/seo.ts 的 isNoindexRoute 统一处理。
// 三处缺一，页面性质就只能靠用户自己猜，所以标题与 h1 都显式写出错误码。
import { computed } from "vue";
import { useRouter } from "vue-router";

const props = withDefaults(
  defineProps<{
    /** 大号展示的错误码，如 "404" */
    code: string;
    /** 主标题（同时是本页唯一的 <h1>） */
    title: string;
    /** 一句话说明，讲清「发生了什么 + 该怎么办」 */
    description: string;
    /** 用户实际请求的地址，展示出来便于核对（可省略） */
    path?: string;
    /** 主色调：404 用品牌色，403 用警告色，两类错误一眼可区分 */
    tone?: "brand" | "warning";
  }>(),
  { path: "", tone: "brand" }
);

const router = useRouter();

// 从站内跳进来才有「上一页」可回；直接粘贴地址打开时 history 只有一条，
// 此时点「返回上一页」会退出站点，所以干脆不显示这个按钮。
const canGoBack = computed(() => typeof window !== "undefined" && window.history.length > 1);

// 用 props 参与模板计算，避免 TS 报「声明未使用」
const code = computed(() => props.code);
</script>

<template>
  <div class="error-page" :class="`error-page--${tone}`">
    <div class="error-page__inner">
      <p class="error-page__code" aria-hidden="true">{{ code }}</p>
      <h1 class="error-page__title">{{ title }}</h1>
      <p class="error-page__desc">{{ description }}</p>

      <p v-if="path" class="error-page__path">
        <span class="error-page__path-label">请求地址</span>
        <code class="error-page__path-value">{{ path }}</code>
      </p>

      <div class="error-page__actions">
        <t-button theme="primary" size="large" @click="router.push('/')">返回首页</t-button>
        <t-button variant="outline" size="large" @click="router.push('/problem')">去题库</t-button>
        <t-button v-if="canGoBack" variant="text" size="large" @click="router.back()">
          返回上一页
        </t-button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.error-page {
  /* 撑起高度并居中：沿用原先两个错误页的 75vh，避免内容过矮时页脚被顶到视口中间 */
  min-height: 400px;
  height: 75vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.error-page__inner {
  width: 100%;
  max-width: 560px;
  text-align: center;
}

/* 颜色一律走 tdesign 的 CSS 变量，深浅色主题下都能自适应 */
.error-page--brand {
  --error-page-accent: var(--td-brand-color);
  --error-page-accent-soft: var(--td-brand-color-3);
}

.error-page--warning {
  --error-page-accent: var(--td-warning-color);
  --error-page-accent-soft: var(--td-warning-color-3);
}

.error-page__code {
  margin: 0;
  font-size: 104px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: 2px;
  /* 先给纯色兜底，再叠一层渐变文字；不支持 background-clip 时仍能看清 */
  color: var(--error-page-accent);
  background-image: linear-gradient(
    135deg,
    var(--error-page-accent),
    var(--error-page-accent-soft)
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.error-page__title {
  margin: 16px 0 0;
  font-size: 22px;
  font-weight: 600;
  color: var(--td-text-color-primary);
}

.error-page__desc {
  margin: 12px 0 0;
  font-size: 14px;
  line-height: 22px;
  color: var(--td-text-color-secondary);
}

.error-page__path {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  margin: 20px 0 0;
  padding: 6px 12px;
  border-radius: 6px;
  background: var(--td-bg-color-secondarycontainer);
  font-size: 12px;
}

.error-page__path-label {
  flex: none;
  color: var(--td-text-color-placeholder);
}

.error-page__path-value {
  min-width: 0;
  /* 地址可能很长，允许在任意位置折行，避免撑破容器 */
  overflow-wrap: anywhere;
  color: var(--td-text-color-primary);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

.error-page__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 28px;
}

/* 移动端：高度改为自适应，按钮竖排铺满，避免 75vh 在小屏上留大片空白 */
@media (max-width: 800px) {
  .error-page {
    height: auto;
    min-height: 60vh;
    padding: 32px 16px;
  }

  .error-page__code {
    font-size: 72px;
  }

  .error-page__title {
    font-size: 18px;
  }

  .error-page__actions {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
